import {
  Component,
  input,
  output,
  signal,
  inject,
  OnInit,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  Persona,
  getNombreCompleto
} from 'src/app/domain/model/persona/persona';
import { PersonaFiltro } from 'src/app/domain/model/persona/persona';
import {
  MAX_IDENTIFICACION_BUSQUEDA,
  identificacionBusquedaKeypress,
  sanitizeIdentificacionBusqueda,
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { EmpleadoRepository } from 'src/app/domain/service/empleado/empleado-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  DEFAULT_ROWS_PER_PAGE,
  TIPO_EMPLEADO
} from 'src/app/common/constants-ejb';
import { busquedaAsesorPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaCursorDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-cursor-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Resultado de asesor seleccionado (lo que recibe el padre).
 */
export interface AsesorSeleccionado {
  nmconsecutivo?: number;
  nmconsecutivoAsesor?: number;
  cdpersona: number;
  cdtipoIdentificacion: string;
  nmidentificacion: string;
  dsnombre1: string;
  dsnombre2: string;
  dsapellido1: string;
  dsapellido2: string;
  dsnombreComercial: string;
  snactivo: string;
}

/**
 * Filtro de búsqueda compartido por los buscadores de asesores.
 * Se reutiliza el mismo contrato que PersonaFiltro para mantener compatibilidad
 * con los componentes que consumen este diálogo.
 */
export type AsesorFiltro = PersonaFiltro;

/** Campos de texto libre del filtro (seguridad-girop §3). */
const CAMPOS_TEXTO_ASESOR = [
  'nombreComercial',
  'nombre1',
  'nombre2',
  'apellido1',
  'apellido2'
] as const;
type CampoTextoAsesor = (typeof CAMPOS_TEXTO_ASESOR)[number];

/**
 * Diálogo SELF-CONTAINED de búsqueda de asesores/consultores.
 *
 * Replica exactamente el legacy GWT: AsesoresSearchViewImpl + PersonSearcherInterfaceImpl
 *
 * Características:
 * - Inyecta EmpleadoRepository internamente (NO recibe datos por @Input)
 * - Maneja su propia paginación con cursores (keyset pagination)
 * - Lógica DT del legacy: si el usuario es Director Técnico -> cdtipo_empleado = null (muestra todos)
 *   si es asesor -> cdtipo_empleado = ASESOR
 * - Solo emite onSelect (asesor seleccionado) y onClose (cerrar diálogo)
 *
 * Uso en padres:
 * ```html
 * @if (dialogBuscarAsesorVisible) {
 *   <app-asesor-search
 *     (onSelect)="seleccionarAsesor($event)"
 *     (onClose)="dialogBuscarAsesorVisible = false" />
 * }
 * ```
 *
 * @author Sura - Migración GWT a Angular
 * Legacy: AsesoresSearchViewImpl.java, PersonSearcherInterfaceImpl.java
 */
@Component({
  selector: 'app-asesor-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    TooltipModule,
    ProgressSpinnerModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [(visible)]="visible"
      (onHide)="onCancelar()"
      header="Parámetros de búsqueda"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '60rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
    >
      <!-- Fila 1: Identificación -->
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4 items-end">
        <div class="sm:col-span-6">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Identificación</label
          >
          <p-dropdown
            [(ngModel)]="filtro.tipoId"
            [options]="tiposIdentificacion()"
            optionLabel="descripcion"
            optionValue="codigo"
            placeholder="--Seleccione--"
            [filter]="true"
            filterBy="descripcion"
            [style]="{ width: '100%' }"
            appendTo="body"
          >
          </p-dropdown>
        </div>
        <div class="sm:col-span-6">
          <input
            pInputText
            [(ngModel)]="filtro.numId"
            placeholder="Número identificación"
            class="w-full"
            [maxlength]="maxIdentificacion"
            (keydown)="soloIdentificacion($event)"
            (input)="sanitizarIdentificacion()"
            (keyup.enter)="buscar()"
          />
        </div>
      </div>

      @if (filtro.tipoId) {
        @if (filtro.tipoId === 'N') {
          <!-- NIT: Nombre comercial -->
          <div class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4">
            <div class="sm:col-span-12">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Nombre comercial</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombreComercial"
                (input)="sanitizarCampo('nombreComercial')"
                placeholder="Nombre comercial"
                class="w-full"
                (keyup.enter)="buscar()"
              />
            </div>
          </div>
        } @else {
          <!-- Persona Natural: Nombres y Apellidos -->
          <div class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4">
            <div class="sm:col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >1er nombre</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombre1"
                (input)="sanitizarCampo('nombre1')"
                class="w-full"
                (keyup.enter)="buscar()"
              />
            </div>
            <div class="sm:col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >2do nombre</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombre2"
                (input)="sanitizarCampo('nombre2')"
                class="w-full"
                (keyup.enter)="buscar()"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4">
            <div class="sm:col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >1er apellido</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.apellido1"
                (input)="sanitizarCampo('apellido1')"
                class="w-full"
                (keyup.enter)="buscar()"
              />
            </div>
            <div class="sm:col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >2do apellido</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.apellido2"
                (input)="sanitizarCampo('apellido2')"
                class="w-full"
                (keyup.enter)="buscar()"
              />
            </div>
          </div>
        }
      }

      <!-- Estado/Activo -->
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4">
        <div class="sm:col-span-12">
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Activo</label
          >
          <div class="flex flex-wrap gap-4 sm:gap-6 pt-2">
            <div class="flex items-center">
              <p-radioButton
                name="estado"
                value="S"
                [(ngModel)]="filtro.activo"
                inputId="activoSi"
              ></p-radioButton>
              <label for="activoSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="estado"
                value="N"
                [(ngModel)]="filtro.activo"
                inputId="activoNo"
              ></p-radioButton>
              <label for="activoNo" class="ml-2 text-sm">No</label>
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="estado"
                value=""
                [(ngModel)]="filtro.activo"
                inputId="activoTodos"
              ></p-radioButton>
              <label for="activoTodos" class="ml-2 text-sm">Todos</label>
            </div>
          </div>
        </div>
      </div>

      <!-- Botones Cerrar, Limpiar y Buscar -->
      <div
        class="flex flex-wrap gap-2 sm:gap-3 mb-4 justify-end sm:justify-start w-full"
      >
        <p-button
          label="Cerrar"
          icon="pi pi-times"
          severity="secondary"
          [outlined]="true"
          size="small"
          styleClass="min-h-[44px]"
          (onClick)="onCancelar()"
        />
        <p-button
          label="Limpiar"
          icon="pi pi-eraser"
          severity="secondary"
          [outlined]="true"
          size="small"
          styleClass="min-h-[44px]"
          (onClick)="limpiarFiltros()"
        />
        <p-button
          label="Buscar"
          icon="pi pi-search"
          size="small"
          styleClass="min-h-[44px]"
          [loading]="loading()"
          (onClick)="buscar()"
        />
      </div>

      <!-- Tabla de resultados -->
      @if (busquedaRealizada()) {
        <!-- Header con conteo de registros -->
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Consultores</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>

        <div class="md:hidden space-y-3 p-3">
          @for (asesor of items(); track asesor.cdpersona ?? $index) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Identificación
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(identificacionAsesor(asesor))"
                >
                  {{ textoTabla(identificacionAsesor(asesor)) }}
                </div>
              </div>

              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                @if (filtro.tipoId === 'N') {
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Nombre Comercial:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(
                        asesor.dsnombre_comercial || asesor.dsnombre_legal
                      )
                    }}</span>
                  </div>
                } @else {
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Nombre Completo:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(getNombreCompleto(asesor))
                    }}</span>
                  </div>
                }
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Activo:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    asesor.snactivo === 'S' ? 'Sí' : 'No'
                  }}</span>
                </div>
              </div>

              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(asesor)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [paginator]="false"
            [rows]="pageSize()"
            [loading]="loading()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Identificación"
                    >Identificación</span
                  >
                </th>
                @if (filtro.tipoId === 'N') {
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre Comercial"
                      >Nombre Comercial</span
                    >
                  </th>
                } @else {
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre Completo"
                      >Nombre Completo</span
                    >
                  </th>
                }
                <th pResizableColumn>
                  <span class="grid-header-text" title="Activo">Activo</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-asesor>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(identificacionAsesor(asesor))"
                    >{{ textoTabla(identificacionAsesor(asesor)) }}</span
                  >
                </td>
                @if (filtro.tipoId === 'N') {
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(
                          asesor.dsnombre_comercial || asesor.dsnombre_legal
                        )
                      "
                      >{{
                        textoTabla(
                          asesor.dsnombre_comercial || asesor.dsnombre_legal
                        )
                      }}</span
                    >
                  </td>
                } @else {
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(getNombreCompleto(asesor))"
                      >{{ textoTabla(getNombreCompleto(asesor)) }}</span
                    >
                  </td>
                }
                <td class="text-center">
                  <span
                    class="grid-cell-text"
                    [attr.title]="asesor.snactivo === 'S' ? 'Sí' : 'No'"
                    >{{ asesor.snactivo === 'S' ? 'Sí' : 'No' }}</span
                  >
                </td>
                <td class="text-center">
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-outlined min-h-[44px]"
                    (click)="seleccionarItem(asesor)"
                  ></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center p-6 text-gray-500">
                  <i class="pi pi-inbox text-4xl mb-3 block"></i>
                  No se encontraron registros
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        @if (totalRegistros() > 0) {
          <app-busqueda-paginador
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="onSelectedPageSizeChange($event); onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }
      }
    </p-dialog>
  `
})
export class BusquedaAsesorDialogComponent
  extends BaseBusquedaCursorDialog<Persona & { nmconsecutivo?: number }>
  implements OnInit
{
  sanitizeText = sanitizeText;

  // === Outputs (lo único que expone al padre) ===
  onSelect = output<AsesorSeleccionado>();
  onClose = output<void>();

  // === Inputs opcionales ===
  titulo = input<string>('');

  // === Services (inyectados internamente -> legacy: PersonSearcherInterfaceImpl) ===
  private readonly empleadoRepository = inject(EmpleadoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly usuarioState = inject(UsuarioStateService);

  // === State ===
  visible = true;
  busquedaRealizada = signal(false);

  /** Longitud máxima del número de identificación (backend: 20). */
  readonly maxIdentificacion = MAX_IDENTIFICACION_BUSQUEDA;

  filtro: AsesorFiltro = {
    tipoId: '',
    numId: '',
    activo: 'S',
    nombre1: '',
    nombre2: '',
    apellido1: '',
    apellido2: '',
    nombreComercial: ''
  };

  // === Catálogos ===
  tiposIdentificacion = signal<CatalogoOption[]>([]);
  private tiposIdMap = signal<Record<string, string>>({});

  // === Paginación keyset ===
  private nextCursor = signal<string | null>(null);
  private previousCursor = signal<string | null>(null);

  readonly tableConfig = computed(() =>
    busquedaAsesorPreset.createConfig({
      style: {
        tableClassName: 'tabla-busqueda-asesor',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron registros'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de asesores',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  // Helpers
  getNombreCompleto = getNombreCompleto;

  ngOnInit(): void {
    this.cargarTiposIdentificacion();
  }

  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);

      const map: Record<string, string> = {};
      tipos.forEach((t) => (map[t.codigo] = t.descripcion));
      this.tiposIdMap.set(map);
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
    }
  }

  getTipoIdLabel(codigo: string | undefined): string {
    if (!codigo) return '';
    return this.tiposIdMap()[codigo] || codigo;
  }

  identificacionAsesor(asesor: Persona): string {
    return `${this.getTipoIdLabel(asesor.cdtipo_identificacion)} - ${asesor.nmidentificacion || ''}`.trim();
  }

  /**
   * Ejecuta búsqueda con los filtros actuales.
   * Legacy: PersonSearcherInterfaceImpl.onSearchClick()
   *
   * Lógica DT del legacy (AsesoresSearchViewImpl.getDTO()):
   * - Si el usuario es Director Técnico -> cdtipo_empleado = null (muestra todos)
   * - Cualquier otro usuario -> cdtipo_empleado = ASESOR
   */
  async buscar(): Promise<void> {
    this.sanitizarFiltros();
    this.busquedaRealizada.set(true);
    await this.ejecutarBusqueda(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS en tiempo real (seguridad-girop §3). Conserva los
   * espacios para no impedir teclear nombres compuestos.
   */
  sanitizarCampo(campo: CampoTextoAsesor): void {
    this.filtro[campo] = sanitizeTextPreserveSpaces(this.filtro[campo]);
  }

  /** Sanitiza (y recorta) los campos de texto libre antes de enviarlos. */
  private sanitizarFiltros(): void {
    for (const campo of CAMPOS_TEXTO_ASESOR) {
      this.filtro[campo] = sanitizeText(this.filtro[campo]);
    }
    this.sanitizarIdentificacion();
  }

  private async ejecutarBusqueda(
    cursor: string | null,
    direction: string,
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    try {
      // Legacy: AsesoresSearchViewImpl.getDTO() L88-94 -> si es DT, cdtipo = null;
      // si no, cdtipo = param CDTIPO_EMP_ASES (respaldo: TIPO_EMPLEADO.ASESOR).
      const cdtipoEmpleado = this.usuarioState.esDirectorTecnico()
        ? undefined
        : (this.usuarioState.cdtipoEmpAses() || TIPO_EMPLEADO.ASESOR);

      const resultado = await this.empleadoRepository.buscar(
        {
          tipoId: this.filtro.tipoId || undefined,
          numId: this.filtro.numId || undefined,
          activo: this.filtro.activo || undefined,
          nombre1: this.filtro.nombre1 || undefined,
          nombre2: this.filtro.nombre2 || undefined,
          apellido1: this.filtro.apellido1 || undefined,
          apellido2: this.filtro.apellido2 || undefined,
          nombreComercial: this.filtro.nombreComercial || undefined,
          cdtipoEmpleado
        },
        this.pageSize(),
        cursor ?? undefined,
        direction
      );

      // Mapear EmpleadoResult -> Persona (para la tabla)
      this.items.set(
        resultado.content.map(
          (e) =>
            ({
              nmconsecutivo: e.nmconsecutivo,
              cdpersona: e.cdpersona,
              cdtipo_identificacion: e.cdtipoIdentificacion,
              nmidentificacion: e.nmidentificacion,
              dsnombre1: e.dsnombre1,
              dsnombre2: e.dsnombre2,
              dsapellido1: e.dsapellido1,
              dsapellido2: e.dsapellido2,
              dsnombre_comercial: e.dsnombreComercial,
              snactivo: e.snactivo
            }) as Persona & { nmconsecutivo?: number }
        )
      );

      this.totalRegistros.set(resultado.totalElements);
      this.nextCursor.set(resultado.nextCursor);
      this.previousCursor.set(resultado.previousCursor);
      // Los flags vienen de la respuesta; la pagina solo cambia tras el exito.
      this.hasNext.set(resultado.hasNext);
      this.hasPrevious.set(resultado.hasPrevious);
      this.currentPage.set(pagina);
    } catch {
      // Como en release: se limpian resultados (el paginador se oculta con total 0);
      // currentPage NO se toca (solo cambia tras una respuesta exitosa).
      this.items.set([]);
      this.totalRegistros.set(0);
      this.hasNext.set(false);
      this.hasPrevious.set(false);
      this.nextCursor.set(null);
      this.previousCursor.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  // === Paginación ===
  irPrimeraPagina(): void {
    if (this.loading()) return;
    this.ejecutarBusqueda(null, 'NEXT', 1);
  }

  irPaginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) return;
    this.ejecutarBusqueda(
      this.previousCursor(),
      'PREVIOUS',
      Math.max(1, this.currentPage() - 1)
    );
  }

  irPaginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) return;
    this.ejecutarBusqueda(this.nextCursor(), 'NEXT', this.currentPage() + 1);
  }

  irUltimaPagina(): void {
    if (this.loading()) return;
    const ultima = Math.max(
      1,
      Math.ceil(this.totalRegistros() / this.pageSize())
    );
    this.ejecutarBusqueda(null, 'LAST', ultima);
  }

  onPageSizeChange(): void {
    if (this.loading()) return;
    this.pageSize.set(this.selectedPageSize);
    this.ejecutarBusqueda(null, 'NEXT', 1);
  }

  // === Acciones ===
  seleccionarItem(asesor: Persona & { nmconsecutivo?: number }): void {
    const consecutivoAsesor = asesor.nmconsecutivo ?? asesor.cdpersona;
    const resultado: AsesorSeleccionado = {
      nmconsecutivo: consecutivoAsesor,
      nmconsecutivoAsesor: consecutivoAsesor,
      cdpersona: asesor.cdpersona!,
      cdtipoIdentificacion: asesor.cdtipo_identificacion || '',
      nmidentificacion: asesor.nmidentificacion || '',
      dsnombre1: asesor.dsnombre1 || '',
      dsnombre2: asesor.dsnombre2 || '',
      dsapellido1: asesor.dsapellido1 || '',
      dsapellido2: asesor.dsapellido2 || '',
      dsnombreComercial: asesor.dsnombre_comercial || '',
      snactivo: asesor.snactivo || ''
    };
    this.onSelect.emit(resultado);
    this.visible = false;
  }

  limpiarFiltros(): void {
    this.filtro = {
      tipoId: '',
      numId: '',
      activo: 'S',
      nombre1: '',
      nombre2: '',
      apellido1: '',
      apellido2: '',
      nombreComercial: ''
    };
    this.busquedaRealizada.set(false);
    this.resetPaginacionCursor();
    this.nextCursor.set(null);
    this.previousCursor.set(null);
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

  /**
   * Número de identificación: letras y dígitos, sin espacios ni separadores.
   * Legacy: IdentificationViewImpl.setRegex() (Constants.REGEX_ALPHANUMERIC).
   */
  soloIdentificacion(event: KeyboardEvent): void {
    if (!identificacionBusquedaKeypress(event)) {
      event.preventDefault();
    }
  }

  /**
   * Limpia el número de identificación (también lo pegado) y lo pasa a mayúsculas.
   * Legacy: IdentificationViewImpl.getDataFromFields() — toUpperCase().
   */
  sanitizarIdentificacion(): void {
    this.filtro.numId = sanitizeIdentificacionBusqueda(this.filtro.numId);
  }
}
